import { zoneFromScore } from "./v2OverviewUtils";

const PLAYER_COLORS = [
  "#7DB7FF", "#A5F3FC", "#C5E86C", "#FFE066", "#D8B4FE",
  "#9C8CFF", "#E879F9", "#4E73F6", "#FF7EC7", "#FFF3B0",
  "#2DC7B6", "#F9A8D4", "#3AA0FF", "#C084FC", "#FFC6E5",
  "#FBB4FE", "#5EEAD4", "#B794FF", "#34CBFF",
];

export function playerColor(id) {
  const text = String(id || "");
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) | 0;
  }
  return PLAYER_COLORS[Math.abs(hash) % PLAYER_COLORS.length];
}

function withStacks(players) {
  const buckets = {};
  return players.map((player) => {
    const key = `${player.zone || "none"}:${Number(player.score || 0).toFixed(1)}`;
    const stack = buckets[key] || 0;
    buckets[key] = stack + 1;
    return { ...player, stack: Math.min(stack, 2) };
  });
}

function formatShortDate(value, locale = "fr") {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(locale.startsWith("en") ? "en-GB" : "fr-FR", {
    day: "2-digit",
    month: "2-digit",
  });
}

export function mapAvailability(data, t, locale) {
  if (!data) return null;
  const available = data.available ?? 0;
  const total = data.total ?? 0;
  return {
    total,
    available,
    absent: data.absent ?? 0,
    absentPlayers: (data.absent_players || []).map((player) => player.name),
    now: data.now_percent,
    nowMeta: t("v2.overview.now_meta", {
      available,
      total,
      date: formatShortDate(data.date, locale),
    }),
    avg7: data.avg_7_percent,
    avg7Delta: data.avg_7_delta,
    avg30: data.avg_30_percent,
    avg30Meta: t("v2.overview.avg_30_range", {
      from: formatShortDate(data.range_from, locale),
      to: formatShortDate(data.range_to, locale),
    }),
    trend: (data.trend || []).map((point) => point.percent),
    trendLabels: (data.trend || []).map((point) => formatShortDate(point.date, locale)),
  };
}

function overviewScore(player) {
  const value = Number(player?.score);
  return Number.isFinite(value) ? value : 0;
}

function overviewZone(player) {
  if (player?.zone) return player.zone;
  return player?.score == null ? "underload" : zoneFromScore(overviewScore(player));
}

export function mapInternalPlayers(players = []) {
  const mapped = players.map((player) => ({
    id: player.id,
    name: player.display_name || `${player.first_name || ""} ${player.last_name || ""}`.trim(),
    position: player.position_code || "MIL",
    number: player.number != null ? Number(player.number) || player.number : null,
    score: overviewScore(player),
    zone: overviewZone(player),
    color: playerColor(player.id),
    fatigue: player.fatigue,
    intensity: player.intensity,
    rii: player.rii,
    load: player.load,
    mli: player.mli,
    metrics: {
      fatigue: { value: player.fatigue, delta: player.fatigue_delta },
      intensity: { value: player.intensity, delta: player.intensity_delta },
      rii: { value: player.rii, delta: player.rii_delta },
      load: { value: player.load, delta: player.load_delta },
      mli: { value: player.mli, delta: player.mli_delta },
    },
    deltas: {
      fatigue: player.fatigue_delta,
      intensity: player.intensity_delta,
      rii: player.rii_delta,
      load: player.load_delta,
      mli: player.mli_delta,
    },
    rationale: player.rationale || "",
    criticality: player.criticality,
    lastOverload: player.last_overload_date
      ? formatShortDate(player.last_overload_date)
      : null,
    overloadDays: player.overload_days,
    overloadDates: player.overload_dates,
    musclesToTreat: player.muscles_to_treat || [],
    focusMuscle: player.focus_muscle,
    focusSide: player.focus_side,
    monthScore: player.month_score,
    simulationId: player.simulation_id,
  }));
  return withStacks(mapped);
}

export function parseSessionId(sessionId) {
  if (!sessionId || !String(sessionId).includes(":")) return { date: null, period: null };
  const [date, period] = String(sessionId).split(":");
  return { date, period };
}

export function toIsoDate(value = new Date()) {
  const date = value instanceof Date ? value : new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function shiftIsoDate(iso, days) {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
}

export function latestSessionForDate(sessions, isoDate) {
  const daySessions = (sessions || []).filter((item) => item.date === isoDate);
  if (!daySessions.length) return null;
  return [...daySessions].sort((left, right) => {
    if (left.period === right.period) return 0;
    return left.period === "afternoon" ? 1 : -1;
  }).at(-1);
}

export function rangeWindow(rangeId, today = new Date()) {
  const iso = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (rangeId === "yesterday") {
    start.setDate(start.getDate() - 1);
    return { from: iso(start), to: iso(start) };
  }
  if (rangeId === "last7") {
    const from = new Date(start);
    from.setDate(from.getDate() - 6);
    return { from: iso(from), to: iso(start) };
  }
  if (rangeId === "last30") {
    const from = new Date(start);
    from.setDate(from.getDate() - 29);
    return { from: iso(from), to: iso(start) };
  }
  return { from: iso(start), to: iso(start) };
}
